// src/components/ToastProvider.jsx
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const T = {
  dark: "#0c1118", amber: "#f9ab14", bg: "#f4f6f8",
  surface: "#ffffff", border: "#e8ecf0", muted: "#6b7280",
};

const PALETTE = {
  success: { bg: "#16a34a", border: "#15803d" },
  error:   { bg: "#e11d48", border: "#be123c" },
  info:    { bg: "#334155", border: "#1e293b" },
  warning: { bg: T.amber,  border: "#d97706" },
};

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(1);

  const remove = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const notify = useCallback((message, opts = {}) => {
    const id = idRef.current++;
    const toast = { id, message, type: opts.type || "success", duration: opts.duration ?? 3500 };
    setToasts((t) => [...t, toast]);
    if (toast.duration > 0) setTimeout(() => remove(id), toast.duration);
  }, [remove]);

  const api = useMemo(() => ({
    show:    (msg, o) => notify(msg, o),
    success: (msg, o) => notify(msg, { ...o, type: "success" }),
    error:   (msg, o) => notify(msg, { ...o, type: "error" }),
    info:    (msg, o) => notify(msg, { ...o, type: "info" }),
    warn:    (msg, o) => notify(msg, { ...o, type: "warning" }),
  }), [notify]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div style={{ position: "fixed", zIndex: 9999, bottom: 24, right: 24, display: "flex", flexDirection: "column", gap: 12 }}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ toast, onClose }) {
  const p = PALETTE[toast.type] || PALETTE.info;
  return (
    <div style={{ background: p.bg, border: `1px solid ${p.border}`, color: "#fff", padding: "12px 16px", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", display: "flex", alignItems: "flex-start", gap: 12, minWidth: 260, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{toast.message}</div>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: 16, lineHeight: 1 }} aria-label="Close">✕</button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
